/**
 * 배정 통계 컴포넌트
 * - Statistic 카드로 통계 표시
 * - 배정 학생 수, 완료 학생 수, 진행률
 */

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Progress, Spin } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import { assignmentService } from '../../services/assignmentService';
import type { AssignmentStats as StatsType } from '../../types/assignment';

interface AssignmentStatsProps {
  workbookName: string;
}

/**
 * AssignmentStats 컴포넌트
 * 문제집 배정 통계
 */
export const AssignmentStats: React.FC<AssignmentStatsProps> = ({ workbookName }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsType | null>(null);

  /**
   * 통계 조회
   */
  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await assignmentService.getAssignmentStats(workbookName);
      setStats(data);
    } catch (error) {
      console.error('통계 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드
   */
  useEffect(() => {
    if (workbookName) {
      fetchStats();
    }
  }, [workbookName]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* 배정된 학생 수 */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="배정된 학생"
              value={stats.assigned_count}
              suffix="명"
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        {/* 완료한 학생 수 */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="완료한 학생"
              value={stats.completed_count}
              suffix="명"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        {/* 진행 중인 학생 수 */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="진행 중"
              value={stats.in_progress_count}
              suffix="명"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>

        {/* 완료율 */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="완료율"
              value={stats.completion_rate}
              suffix="%"
              prefix={<PercentageOutlined />}
              valueStyle={{ color: stats.completion_rate >= 80 ? '#52c41a' : '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 진행률 바 */}
      <Card style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <strong>전체 진행률</strong>
        </div>
        <Progress
          percent={stats.completion_rate}
          status={stats.completion_rate === 100 ? 'success' : 'active'}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
        />
        <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
          {stats.completed_count} / {stats.assigned_count}명 완료
        </div>
      </Card>
    </div>
  );
};
